import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

export const loadWebsites = createAsyncThunk(
    'website/loadWebsites',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/websites');
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Не удалось загрузить список сайтов');
        }
    }
);

// brief опционален, но с ним результат заметно точнее.
// logo это File, images массив File. С файлами запрос уходит multipart,
// brief тогда едет JSON-строкой. Без файлов обычным JSON.
//
// Сервер отвечает id фоновой задачи, а сам сайт собирается 1-3 минуты.
// Поэтому опрашиваем статус и отдаём шаги в onStep, чтобы показать прогресс.
// Колбэк в аргументе thunk не мешает Redux: meta.arg не проверяется на сериализуемость.
const POLL_MS = 2000;
const POLL_LIMIT_MS = 10 * 60 * 1000;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const generateWebsite = createAsyncThunk(
    'website/generateWebsite',
    async ({ prompt, brief, logo = null, images = [], onStep }, { rejectWithValue, getState }) => {
        try {
            // Язык приложения: сайт генерируется на нём, это его основной язык
            const lang = (getState().language && getState().language.currentLanguage) || 'ru';
            let body = { prompt, brief, lang };

            if (logo || images.length) {
                body = new FormData();
                body.append('prompt', prompt);
                body.append('brief', JSON.stringify(brief || {}));
                body.append('lang', lang);
                if (logo) body.append('logo', logo);
                images.forEach((file) => body.append('images', file));
            }

            const response = await api.post('/websites/generate', body);
            const started = response.data.data || {};

            // Старый сервер отвечал сразу готовым сайтом
            if (started._id) return started;
            if (!started.jobId) return rejectWithValue('Не удалось создать сайт');

            if (onStep && started.stepText) onStep(started.stepText);

            const deadline = Date.now() + POLL_LIMIT_MS;
            let misses = 0;

            while (Date.now() < deadline) {
                await sleep(POLL_MS);

                let job;
                try {
                    const r = await api.get(`/websites/jobs/${started.jobId}`);
                    job = r.data.data;
                    misses = 0;
                } catch (err) {
                    // Короткий обрыв связи не повод сдаваться
                    misses += 1;
                    if (misses >= 5) throw err;
                    continue;
                }

                if (onStep && job.stepText) onStep(job.stepText);
                if (job.status === 'done' && job.website) {
                    return job.notice ? { ...job.website, notice: job.notice } : job.website;
                }
                if (job.status === 'failed') return rejectWithValue(job.error || 'Не удалось создать сайт');
            }

            return rejectWithValue('Сайт собирается слишком долго. Проверьте раздел «Мои сайты» через пару минут');
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Не удалось создать сайт');
        }
    }
);

export const loadWebsite = createAsyncThunk(
    'website/loadWebsite',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/websites/${id}`);
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Не удалось загрузить сайт');
        }
    }
);

export const updateWebsite = createAsyncThunk(
    'website/updateWebsite',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/websites/${id}`, data);
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Не удалось сохранить изменения');
        }
    }
);

export const regenerateSection = createAsyncThunk(
    'website/regenerateSection',
    async ({ id, sectionId, hint }, { rejectWithValue }) => {
        try {
            const response = await api.post(`/websites/${id}/sections/${sectionId}/regenerate`, { hint });
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Не удалось перегенерировать секцию');
        }
    }
);

export const deleteWebsite = createAsyncThunk(
    'website/deleteWebsite',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/websites/${id}`);
            return id;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Не удалось удалить сайт');
        }
    }
);

export const publicWebsite = createAsyncThunk(
    'website/publicWebsite',
    async (id, { rejectWithValue, getState }) => {
        try {
            const state = getState();
            const userId = state.auth.user;
        
            if (!userId) {
                throw new Error('Пользователь не аутентифицирован');
            }
            const response = await api.post(`/public/${id}/publish`);
            return response.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.error || 'Не удалось сохранить изменения');
        }
    }
);

// Начальное состояние
const initialState = {
    websites: [],
    currentWebsite: null,
    loading: false,
    saving: false,
    error: null,
    isEditing: false
};

// Слайс
const websiteSlice = createSlice({
    name: 'website',
    initialState,
    reducers: {
        setCurrentWebsite: (state, action) => {
            state.currentWebsite = action.payload;
        },
        setIsEditing: (state, action) => {
            state.isEditing = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        // Локальная замена одной записи (без перезапроса)
        replaceInList: (state, action) => {
            const site = action.payload;
            const index = state.websites.findIndex(
                (w) => String(w._id) === String(site._id)
            );
            
            if (index === -1) {
                state.websites = [site, ...state.websites];
            } else {
                state.websites[index] = site;
            }
            
            if (state.currentWebsite && String(state.currentWebsite._id) === String(site._id)) {
                state.currentWebsite = site;
            }
        },
    },
    extraReducers: (builder) => {
        builder
        // loadWebsites
        .addCase(loadWebsites.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(loadWebsites.fulfilled, (state, action) => {
            state.loading = false;
            state.websites = action.payload;
        })
        .addCase(loadWebsites.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        
        // generateWebsite
        .addCase(generateWebsite.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(generateWebsite.fulfilled, (state, action) => {
            state.loading = false;
            const newWebsite = action.payload;
            state.currentWebsite = newWebsite;
            state.websites = [newWebsite, ...state.websites];
        })
        .addCase(generateWebsite.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        
        // loadWebsite
        .addCase(loadWebsite.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
        .addCase(loadWebsite.fulfilled, (state, action) => {
            state.loading = false;
            const site = action.payload;
            state.currentWebsite = site;
            
            // Обновляем в списке
            const index = state.websites.findIndex(
                (w) => String(w._id) === String(site._id)
            );
            if (index === -1) {
                state.websites = [site, ...state.websites];
            } else {
                state.websites[index] = site;
            }
        })
        .addCase(loadWebsite.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
        })
        
        // updateWebsite (используем saving, а не loading)
        .addCase(updateWebsite.pending, (state) => {
            state.saving = true;
            state.error = null;
        })
        .addCase(updateWebsite.fulfilled, (state, action) => {
            state.saving = false;
            const site = action.payload;
            
            // Обновляем в списке
            const index = state.websites.findIndex(
                (w) => String(w._id) === String(site._id)
            );
            if (index === -1) {
                state.websites = [site, ...state.websites];
            } else {
                state.websites[index] = site;
            }
            
            if (state.currentWebsite && String(state.currentWebsite._id) === String(site._id)) {
                state.currentWebsite = site;
            }
        })
        .addCase(updateWebsite.rejected, (state, action) => {
            state.saving = false;
            state.error = action.payload;
        })
        
        // regenerateSection
        .addCase(regenerateSection.pending, (state) => {
            state.saving = true;
            state.error = null;
        })
        .addCase(regenerateSection.fulfilled, (state, action) => {
            state.saving = false;
            const site = action.payload;
            
            const index = state.websites.findIndex(
                (w) => String(w._id) === String(site._id)
            );
            if (index === -1) {
                state.websites = [site, ...state.websites];
            } else {
                state.websites[index] = site;
            }
            
            if (state.currentWebsite && String(state.currentWebsite._id) === String(site._id)) {
                state.currentWebsite = site;
            }
        })
        .addCase(regenerateSection.rejected, (state, action) => {
            state.saving = false;
            state.error = action.payload;
        })
        
        // deleteWebsite
        .addCase(deleteWebsite.pending, (state) => {
            state.saving = true;
            state.error = null;
        })
        .addCase(deleteWebsite.fulfilled, (state, action) => {
            state.saving = false;
            const id = action.payload;
            state.websites = state.websites.filter(
                (w) => String(w._id) !== String(id)
            );
            if (state.currentWebsite && String(state.currentWebsite._id) === String(id)) {
                state.currentWebsite = null;
            }
        })
        .addCase(deleteWebsite.rejected, (state, action) => {
            state.saving = false;
            state.error = action.payload;
        })

        // publicWebsite
        .addCase(publicWebsite.pending, (state) => {
            state.saving = true;
            state.error = null;
        })
        .addCase(publicWebsite.fulfilled, (state, action) => {
            state.saving = false;
            const site = action.payload;

            // Обновляем в списке
            const index = state.websites.findIndex(
                (w) => String(w._id) === String(site._id)
            );
            if (index === -1) {
                state.websites = [site, ...state.websites];
            } else {
                state.websites[index] = site;
            }
            
        })
        .addCase(publicWebsite.rejected, (state, action) => {
            state.saving = false;
            state.error = action.payload;
        });
    },
});

// Экспорт actions
export const { 
    setCurrentWebsite, 
    setIsEditing, 
    clearError, 
    replaceInList,
} = websiteSlice.actions;

// Экспорт селекторов
export const selectWebsites = (state) => state.website.websites;
export const selectCurrentWebsite = (state) => state.website.currentWebsite;
export const selectLoading = (state) => state.website.loading;
export const selectSaving = (state) => state.website.saving;
export const selectError = (state) => state.website.error;
export const selectIsEditing = (state) => state.website.isEditing;

// Экспорт редьюсера
export default websiteSlice.reducer;